import { useEffect, useState } from 'react';

import { css } from '@emotion/css';
import { Row, Col, Space } from 'antd';
import { isNumber } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { doGetQuoteChart } from 'src/actions';
import { QuoteChartQueryParamIntervalDto } from 'src/dtos';
import { MSpinner, MTypography, PercentageField, Chart } from 'src/lib';
import { mapQuoteChartQueryParamIntervalDtoToChartOptions } from 'src/mappers';
import { Quote, QuoteChart } from 'src/models';
import { BaseStyle, Color } from 'src/styles';
import { Maybe } from 'src/utils';

interface SecurityDetailsChartFilterIntervalFieldProps {
  value: string;
  isActive?: boolean;
}

export const SecurityDetailsChartFilterIntervalField = ({
  value,
  isActive = false,
}: SecurityDetailsChartFilterIntervalFieldProps) => {
  return (
    <MTypography variant='subtitle1' color={Color.Brand6} weight={isActive ? 'bold' : 'normal'}>
      {value}
    </MTypography>
  );
};

const securityDetailsChartFilterSpaceStyle = css`
  cursor: pointer;
`;

interface SecurityDetailsChartFilterProps {
  value: QuoteChartQueryParamIntervalDto;
  onChange: (value: QuoteChartQueryParamIntervalDto) => void;
}

export const SecurityDetailsChartFilter = ({ value, onChange }: SecurityDetailsChartFilterProps) => {
  return (
    <Row className={BaseStyle.level}>
      <Space size={16} className={securityDetailsChartFilterSpaceStyle}>
        <Col onClick={() => onChange(QuoteChartQueryParamIntervalDto.OneDay)}>
          <SecurityDetailsChartFilterIntervalField
            value='1 Day'
            isActive={value === QuoteChartQueryParamIntervalDto.OneDay}
          />
        </Col>
        <Col onClick={() => onChange(QuoteChartQueryParamIntervalDto.OneWeek)}>
          <SecurityDetailsChartFilterIntervalField
            value='1 Week'
            isActive={value === QuoteChartQueryParamIntervalDto.OneWeek}
          />
        </Col>
        <Col onClick={() => onChange(QuoteChartQueryParamIntervalDto.OneMonth)}>
          <SecurityDetailsChartFilterIntervalField
            value='1 Month'
            isActive={value === QuoteChartQueryParamIntervalDto.OneMonth}
          />
        </Col>
        <Col onClick={() => onChange(QuoteChartQueryParamIntervalDto.SixMonths)}>
          <SecurityDetailsChartFilterIntervalField
            value='6 Months'
            isActive={value === QuoteChartQueryParamIntervalDto.SixMonths}
          />
        </Col>
        <Col onClick={() => onChange(QuoteChartQueryParamIntervalDto.OneYear)}>
          <SecurityDetailsChartFilterIntervalField
            value='1 Year'
            isActive={value === QuoteChartQueryParamIntervalDto.OneYear}
          />
        </Col>
      </Space>
    </Row>
  );
};

interface SecurityDetailsChartProps {
  symbol?: string;
}

export const SecurityDetailsChart = ({ symbol }: SecurityDetailsChartProps) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state: any) => Boolean(state.quotes.chart.isLoading));
  const chartData: Maybe<QuoteChart[]> = useSelector((state: any) => state.quotes.chart.data?.data);
  const quoteDetails: Maybe<Quote> = useSelector((state: any) => state.quotes.details.data);
  const [filter, setFilter] = useState<QuoteChartQueryParamIntervalDto>(QuoteChartQueryParamIntervalDto.OneDay);
  const [series, setSeries] = useState<[number, number][]>([]);

  useEffect(() => {
    setSeries(chartData?.map(item => [new Date(item.date).valueOf(), item.closePrice]) ?? []);
  }, [chartData]);

  useEffect(() => {
    if (symbol) {
      dispatch(
        doGetQuoteChart({
          params: { symbol },
          queryParams: {
            interval: filter,
          },
        }),
      );
    }
  }, [symbol]);

  return (
    <Row>
      {isNumber(quoteDetails?.change) && (
        <Col span={24}>
          <MTypography variant='h6' color={Color.Brand6} align='center'>
            <PercentageField
              value={quoteDetails?.change}
              color={(quoteDetails?.change ?? 0) >= 0 ? Color.Green6 : Color.Red6}
            />
          </MTypography>
        </Col>
      )}
      <Col span={24}>
        <SecurityDetailsChartFilter
          value={filter}
          onChange={value => {
            setFilter(value);

            if (symbol) {
              dispatch(doGetQuoteChart({ params: { symbol }, queryParams: { interval: value } }));
            }
          }}
        />
      </Col>
      <Col span={24} className={BaseStyle.level}>
        {isLoading ? (
          <MSpinner height='275px' />
        ) : (
          <Chart
            data={series}
            width={490}
            height={275}
            options={mapQuoteChartQueryParamIntervalDtoToChartOptions(filter)}
          />
        )}
      </Col>
    </Row>
  );
};
